import type { ChangeFormField, ChangeSubtask } from '../../types/changes';
import { hydrateExtraFields } from './changeFormSchema';
import { formatChecklistAnswer } from './components/ChangeSubtaskChecklist';

export const INTERNAL_SUBTASK_TABLE_COLUMNS_STORAGE_KEY =
  'changes_internal_subtask_table_columns_v1';

export type InternalSubtaskCoreColumnId =
  | 'title'
  | 'group_name'
  | 'status'
  | 'member'
  | 'task_type';

export type InternalSubtaskTableColumnId =
  | InternalSubtaskCoreColumnId
  | `field:${number}`
  | `preset:${string}`;

export type InternalSubtaskTableColumnGroupId = 'core' | 'checklist';

export type InternalSubtaskTableColumnOption = {
  id: InternalSubtaskTableColumnId;
  label: string;
  group: InternalSubtaskTableColumnGroupId;
  hint?: string;
};

export const INTERNAL_SUBTASK_CORE_COLUMN_OPTIONS: InternalSubtaskTableColumnOption[] = [
  { id: 'title', label: 'Subtarefa', group: 'core' },
  { id: 'group_name', label: 'Grupo associado', group: 'core' },
  { id: 'status', label: 'Status', group: 'core' },
  { id: 'member', label: 'Membro do grupo', group: 'core' },
  { id: 'task_type', label: 'Tipo da tarefa', group: 'core' },
];

export const INTERNAL_SUBTASK_DEFAULT_TABLE_COLUMNS: InternalSubtaskTableColumnId[] =
  INTERNAL_SUBTASK_CORE_COLUMN_OPTIONS.map((item) => item.id);

const CORE_IDS = new Set<string>(INTERNAL_SUBTASK_DEFAULT_TABLE_COLUMNS);

const FIELD_PREFIX = 'field:';
const PRESET_PREFIX = 'preset:';

function isAllowedColumnId(id: string): boolean {
  if (CORE_IDS.has(id)) return true;
  if (parseInternalSubtaskFieldColumnId(id) != null) return true;
  if (id.startsWith(PRESET_PREFIX) && id.length > PRESET_PREFIX.length) return true;
  return false;
}

export function isInternalSubtask(subtask: Pick<ChangeSubtask, 'origin'>): boolean {
  return subtask.origin === 'checklist';
}

export function parseInternalSubtaskFieldColumnId(
  columnId: string,
): number | null {
  if (!columnId.startsWith(FIELD_PREFIX)) return null;
  const raw = columnId.slice(FIELD_PREFIX.length);
  if (!/^\d+$/.test(raw)) return null;
  const id = Number(raw);
  return Number.isFinite(id) && id > 0 ? id : null;
}

export function internalSubtaskFieldColumnId(fieldId: number): InternalSubtaskTableColumnId {
  return `${FIELD_PREFIX}${fieldId}`;
}

export function checklistFieldsFromSubtask(subtask: ChangeSubtask): ChangeFormField[] {
  return (subtask.checklist?.items || []).map((item) => ({
    ...item,
    kind: item.kind || 'extra',
  }));
}

export function buildInternalSubtaskColumnCatalog(
  subtasks: ChangeSubtask[],
): InternalSubtaskTableColumnOption[] {
  const fieldMap = new Map<number, ChangeFormField>();
  subtasks.forEach((subtask) => {
    checklistFieldsFromSubtask(subtask).forEach((field) => {
      if (!field.id) return;
      if (!fieldMap.has(field.id)) {
        fieldMap.set(field.id, field);
      }
    });
  });
  const checklistOptions = Array.from(fieldMap.values())
    .sort((a, b) => {
      const orderA = a.sort_order ?? 0;
      const orderB = b.sort_order ?? 0;
      if (orderA !== orderB) return orderA - orderB;
      return String(a.label).localeCompare(String(b.label), 'pt-BR');
    })
    .map((field) => ({
      id: internalSubtaskFieldColumnId(field.id),
      label: field.label,
      group: 'checklist' as const,
      hint: 'Campo do checklist desta subtarefa interna',
    }));
  return [...INTERNAL_SUBTASK_CORE_COLUMN_OPTIONS, ...checklistOptions];
}

export function catalogColumnIds(
  catalog: InternalSubtaskTableColumnOption[],
): InternalSubtaskTableColumnId[] {
  return catalog.map((item) => item.id);
}

function catalogOrder(
  ids: readonly string[],
  catalogIds: readonly string[],
): InternalSubtaskTableColumnId[] {
  const allowed = new Set(catalogIds);
  const ordered: InternalSubtaskTableColumnId[] = [];
  ids.forEach((entry) => {
    if (!allowed.has(entry)) return;
    const columnId = entry as InternalSubtaskTableColumnId;
    if (ordered.includes(columnId)) return;
    ordered.push(columnId);
  });
  return ordered;
}

export function sanitizeInternalSubtaskTableColumns(
  value: unknown,
  catalogIds: readonly string[],
): InternalSubtaskTableColumnId[] {
  const allowed = new Set(catalogIds);
  const asArray = Array.isArray(value) ? value : [];
  const seen = new Set<string>();
  const picked: string[] = [];
  asArray.forEach((entry) => {
    if (typeof entry !== 'string') return;
    const id = entry.trim();
    if (!id || !allowed.has(id) || seen.has(id)) return;
    if (isAllowedColumnId(id)) {
      seen.add(id);
      picked.push(id);
    }
  });
  const ordered = catalogOrder(picked, catalogIds);
  const coreVisible = ordered.filter((id) => CORE_IDS.has(id));
  if (coreVisible.length) return ordered;
  const fallback = INTERNAL_SUBTASK_DEFAULT_TABLE_COLUMNS.filter(
    (id) => !catalogIds.length || catalogIds.includes(id),
  );
  return fallback.length ? fallback : [...INTERNAL_SUBTASK_DEFAULT_TABLE_COLUMNS];
}

type StoredTableColumns = {
  selected?: unknown;
  catalogIds?: unknown;
};

const parseStoredTableColumns = (
  raw: string | null,
  catalogIds: readonly string[],
): InternalSubtaskTableColumnId[] | null => {
  if (raw == null) return null;
  const parsed = JSON.parse(raw) as unknown;
  if (Array.isArray(parsed)) {
    return sanitizeInternalSubtaskTableColumns(parsed, catalogIds);
  }
  if (!parsed || typeof parsed !== 'object') return null;
  const stored = parsed as StoredTableColumns;
  const known = Array.isArray(stored.catalogIds)
    ? stored.catalogIds.filter((id): id is string => typeof id === 'string')
    : catalogIds;
  return sanitizeInternalSubtaskTableColumns(stored.selected, known);
};

export function loadInternalSubtaskTableColumns(
  catalogIds: readonly string[],
): InternalSubtaskTableColumnId[] {
  if (typeof window === 'undefined') {
    return sanitizeInternalSubtaskTableColumns(
      INTERNAL_SUBTASK_DEFAULT_TABLE_COLUMNS,
      catalogIds,
    );
  }
  try {
    const loaded = parseStoredTableColumns(
      window.localStorage.getItem(INTERNAL_SUBTASK_TABLE_COLUMNS_STORAGE_KEY),
      catalogIds,
    );
    return (
      loaded ||
      sanitizeInternalSubtaskTableColumns(INTERNAL_SUBTASK_DEFAULT_TABLE_COLUMNS, catalogIds)
    );
  } catch {
    return sanitizeInternalSubtaskTableColumns(
      INTERNAL_SUBTASK_DEFAULT_TABLE_COLUMNS,
      catalogIds,
    );
  }
}

export function saveInternalSubtaskTableColumns(
  selected: string[],
  catalogIds: readonly string[],
): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      INTERNAL_SUBTASK_TABLE_COLUMNS_STORAGE_KEY,
      JSON.stringify({
        selected: sanitizeInternalSubtaskTableColumns(selected, catalogIds),
        catalogIds: [...catalogIds],
      }),
    );
  } catch {
    // quota / modo privado
  }
}

export function toggleInternalSubtaskTableColumn(
  selected: string[],
  columnId: string,
  catalogIds: readonly string[],
): InternalSubtaskTableColumnId[] {
  const sanitized = sanitizeInternalSubtaskTableColumns(selected, catalogIds);
  if (!catalogIds.includes(columnId)) return sanitized;
  if (sanitized.includes(columnId as InternalSubtaskTableColumnId)) {
    if (sanitized.filter((id) => CORE_IDS.has(id)).length <= 1 && CORE_IDS.has(columnId)) {
      return sanitized;
    }
    return sanitized.filter((id) => id !== columnId);
  }
  return catalogOrder([...sanitized, columnId], catalogIds);
}

export function reorderInternalSubtaskTableColumns(
  selected: string[],
  activeId: string,
  overId: string,
  catalogIds: readonly string[],
): InternalSubtaskTableColumnId[] {
  const sanitized = sanitizeInternalSubtaskTableColumns(selected, catalogIds);
  const oldIndex = sanitized.indexOf(activeId as InternalSubtaskTableColumnId);
  const newIndex = sanitized.indexOf(overId as InternalSubtaskTableColumnId);
  if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return sanitized;
  const next = [...sanitized];
  const [moved] = next.splice(oldIndex, 1);
  next.splice(newIndex, 0, moved);
  return sanitizeInternalSubtaskTableColumns(next, catalogIds);
}

export function mergeInternalSubtaskTableColumns(
  selected: string[],
  catalogIds: readonly string[],
): InternalSubtaskTableColumnId[] {
  return sanitizeInternalSubtaskTableColumns(selected, catalogIds);
}

export function internalSubtaskChecklistCellText(
  subtask: ChangeSubtask,
  fieldId: number,
): string {
  const items = checklistFieldsFromSubtask(subtask);
  const field = items.find((item) => item.id === fieldId);
  if (!field) return '—';
  const extras = hydrateExtraFields(items, subtask.checklist?.answers);
  const value = extras[String(fieldId)];
  const formatted = formatChecklistAnswer(field, value);
  if (formatted === 'Ainda sem resposta') return '—';
  return formatted;
}

export function internalSubtaskCoreColumnLabel(
  columnId: string,
): string | null {
  const core = INTERNAL_SUBTASK_CORE_COLUMN_OPTIONS.find((item) => item.id === columnId);
  return core?.label ?? null;
}

export function fieldLabelByColumnId(
  columnId: InternalSubtaskTableColumnId,
  catalog: InternalSubtaskTableColumnOption[],
): string {
  const fromCatalog = catalog.find((item) => item.id === columnId)?.label;
  if (fromCatalog) return fromCatalog;
  const fromCore = internalSubtaskCoreColumnLabel(columnId);
  if (fromCore) return fromCore;
  return columnId;
}
